import React, { useEffect, useRef, useCallback } from 'react';
import { WaveParams, WaveConfig, SineWaveComposition } from './config';
import './styles.css';

/**
 * WaveBackground Component
 *
 * Creates an animated wave effect using multiple parallel lines with carefully designed
 * opacity and color gradients to create a 3D tubular appearance.
 *
 * The wave effect is achieved through two main visual techniques:
 *
 * 1. Vertical Opacity Gradient:
 *    - Multiple parallel lines are drawn with varying opacity
 *    - Lines are spaced evenly using renderConfig.lineSpacing
 *    - Opacity follows a linear gradient from edges to center:
 *      - Edge lines: 5% opacity (0.05)
 *      - Center line: 35% opacity (0.35)
 *      - Linear interpolation between edges and center
 *    - This creates a subtle 3D tube effect without harsh transitions
 *
 * 2. Horizontal Color Gradient:
 *    - Each line uses the same horizontal color gradient
 *    - Gradient progresses through carefully chosen blue shades:
 *      - 0%:  #050A14 - Nearly black with slight blue tint
 *      - 20%: #0A1A2B - Very dark blue-black
 *      - 40%: #1A3251 - Dark blue
 *      - 70%: #2B5486 - Medium-dark blue
 *      - 90%: #4A90E2 - Bright blue
 *      - 100%: #66A5F2 - Brightest, saturated blue
 *    - More color stops in darker range for subtle transition
 *    - Compressed bright range creates dramatic endpoint
 *
 * The combination of these gradients creates a sophisticated wave effect that:
 * - Appears as a 3D tube through opacity variation
 * - Has depth and drama through the color progression
 * - Maintains smooth transitions without harsh edges
 * - Creates visual interest through contrast between dark and light
 */

interface WaveBackgroundProps {
  config?: WaveConfig; // For control panel mode
  startConfig?: WaveConfig; // For animated mode
  endConfig?: WaveConfig;   // For animated mode
  t?: number;              // For animated mode (legacy)
  cycleConfig?: {
    cycleDuration?: number;
    startWaitTime?: number;
    endWaitTime?: number;
    transitionTime?: number;
  };
}

interface PerformanceMetrics {
  lastFrameTime: number;
  frameCount: number;
  frameTimeSum: number;
  lastFpsUpdate: number;
  renderCount: number;
  waveCalculationTime: number;
  stateUpdateCount: number;
}

// Performance optimization constants
const DEBUG_PERFORMANCE = false;

const logMetrics = (metrics: PerformanceMetrics) => {
  if (!window.debug) return;

  const avgFrameTime = metrics.frameTimeSum / Math.max(1, metrics.frameCount);
  const fps = metrics.frameCount > 0 ? 1000 / avgFrameTime : 0;

  window.debug('Wave Background Performance:');
  window.debug(`FPS: ${fps.toFixed(2)}`);
  window.debug(`Frame Time: ${avgFrameTime.toFixed(2)}ms`);
  window.debug(`Wave Calc Time: ${metrics.waveCalculationTime.toFixed(2)}ms`);
  window.debug(`State Updates: ${metrics.stateUpdateCount}`);
  window.debug(`Render Count: ${metrics.renderCount}`);
};

// Calculate maximum possible amplitude for a wave
const calculateMaxAmplitude = (wave: WaveParams, sineWaves: {
  primary: SineWaveComposition;
  secondary: SineWaveComposition;
  tertiary: SineWaveComposition;
}) => {
  // Sum the maximum possible amplitudes of all component waves
  const primaryMax = wave.amplitude * sineWaves.primary.amplitude;
  const secondaryMax = wave.amplitude * sineWaves.secondary.amplitude;
  const tertiaryMax = wave.amplitude * sineWaves.tertiary.amplitude;

  // Since all waves could theoretically align at their peaks,
  // the maximum total amplitude is the sum of individual maximums
  return primaryMax + secondaryMax + tertiaryMax;
};

// Add these helper functions at the top of the file after imports
const hexToRGB = (hex: string): [number, number, number] => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

/**
 * Creates a horizontal linear gradient with opacity variation.
 * Lets the canvas handle color interpolation between start and end points.
 */
const createHorizontalGradient = (
  ctx: CanvasRenderingContext2D,
  startColor: string,
  endColor: string,
  index: number
): CanvasGradient => {
  const canvas = ctx.canvas;
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);

  const startRGB = hexToRGB(startColor);
  const endRGB = hexToRGB(endColor);

  // Set just two stops with appropriate opacity
  gradient.addColorStop(0, `rgba(${startRGB[0]}, ${startRGB[1]}, ${startRGB[2]}, 0.2)`);
  gradient.addColorStop(1, `rgba(${endRGB[0]}, ${endRGB[1]}, ${endRGB[2]}, 0.9)`);
  return gradient;
};

// Linear interpolation between two numbers
const lerp = (a: number, b: number, t: number): number => a * (1 - t) + b * t;

// Interpolate between two wave configurations
const lerpConfig = (start: WaveConfig, end: WaveConfig, t: number): WaveConfig => {
  return {
    waves: start.waves.map((startWave, index) => {
      const endWave = end.waves[index];
      return {
        ...startWave,
        amplitude: lerp(startWave.amplitude, endWave.amplitude, t),
        frequency: lerp(startWave.frequency, endWave.frequency, t),
        speed: lerp(startWave.speed, endWave.speed, t),
        startColor: startWave.startColor, // Color interpolation not implemented
        endColor: startWave.endColor
      };
    }),
    globalSpeed: lerp(start.globalSpeed, end.globalSpeed, t),
    numWaves: start.numWaves,
    sineWaves: {
      primary: {
        frequency: lerp(start.sineWaves.primary.frequency, end.sineWaves.primary.frequency, t),
        speed: lerp(start.sineWaves.primary.speed, end.sineWaves.primary.speed, t),
        amplitude: lerp(start.sineWaves.primary.amplitude, end.sineWaves.primary.amplitude, t)
      },
      secondary: {
        frequency: lerp(start.sineWaves.secondary.frequency, end.sineWaves.secondary.frequency, t),
        speed: lerp(start.sineWaves.secondary.speed, end.sineWaves.secondary.speed, t),
        amplitude: lerp(start.sineWaves.secondary.amplitude, end.sineWaves.secondary.amplitude, t)
      },
      tertiary: {
        frequency: lerp(start.sineWaves.tertiary.frequency, end.sineWaves.tertiary.frequency, t),
        speed: lerp(start.sineWaves.tertiary.speed, end.sineWaves.tertiary.speed, t),
        amplitude: lerp(start.sineWaves.tertiary.amplitude, end.sineWaves.tertiary.amplitude, t)
      }
    },
    renderConfig: start.renderConfig
  };
};

function get2dContext(canvas: HTMLCanvasElement | null): CanvasRenderingContext2D | null {
  if (!canvas) return null;
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}

const WaveBackground: React.FC<WaveBackgroundProps> = ({ config, startConfig, endConfig, t, cycleConfig }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const timeRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<PerformanceMetrics>({
    lastFrameTime: performance.now(),
    frameCount: 0,
    frameTimeSum: 0,
    lastFpsUpdate: 0,
    renderCount: 0,
    waveCalculationTime: 0,
    stateUpdateCount: 0
  });

  // Get theme background color from CSS variable
  const getThemeBackgroundColor = useCallback(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    const color = computedStyle.getPropertyValue('--color-background-main').trim() || '#000000';
    return color;
  }, []);

  // Log performance metrics every second
  useEffect(() => {
    if (!DEBUG_PERFORMANCE) return;

    const logInterval = setInterval(() => {
      logMetrics(metricsRef.current);

      // Reset counters
      metricsRef.current.frameCount = 0;
      metricsRef.current.frameTimeSum = 0;
      metricsRef.current.stateUpdateCount = 0;
      metricsRef.current.waveCalculationTime = 0;
      metricsRef.current.renderCount = 0;
    }, 1000);

    return () => clearInterval(logInterval);
  }, []);

  // Animation/interpolation logic
  // If startConfig, endConfig, and cycleConfig are provided, handle animation internally
  const defaultCycleConfig = {
    cycleDuration: 30000,
    startWaitTime: 0.4,
    endWaitTime: 0.3,
    transitionTime: 0.1,
  };
  const mergedCycleConfig = { ...defaultCycleConfig, ...cycleConfig };

  // Internal ref to hold the current animated config
  const animatedConfigRef = useRef<WaveConfig | undefined>(undefined);

  // Animation loop for animated mode
  useEffect(() => {
    if (!(startConfig && endConfig && cycleConfig)) return;
    let running = true;
    const startTime = Date.now();
    const animate = () => {
      const now = Date.now();
      const elapsed = (now - startTime) % mergedCycleConfig.cycleDuration;
      const cycleTime = elapsed / mergedCycleConfig.cycleDuration;
      let t = 0;
      // Easing and cycle timing logic
      const { startWaitTime, endWaitTime, transitionTime } = mergedCycleConfig;
      const startHold = startWaitTime;
      const transToEnd = startWaitTime + transitionTime;
      const endHold = transToEnd + endWaitTime;
      const transToStart = endHold + transitionTime;
      if (cycleTime < startHold) {
        t = 0;
      } else if (cycleTime < transToEnd) {
        t = cubicEase((cycleTime - startHold) / transitionTime);
      } else if (cycleTime < endHold) {
        t = 1;
      } else if (cycleTime < transToStart) {
        t = cubicEase(1 - (cycleTime - endHold) / transitionTime);
      } else {
        t = 0;
      }
      animatedConfigRef.current = lerpConfig(startConfig, endConfig, t);
      // Draw frame
      drawFrame();
      if (running) animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      running = false;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startConfig, endConfig, cycleConfig]);

  // Cubic easing function
  const cubicEase = (t: number): number => t * t * (3 - 2 * t);

  // DrawWave now takes config as argument
  const drawWave = useCallback((ctx: CanvasRenderingContext2D, wave: WaveParams, waveY: number, index: number, configToUse: WaveConfig) => {
    const points: [number, number][] = [];
    const canvas = ctx.canvas;
    for (let x = 0; x <= canvas.width; x += 6) {
      const primaryWave = Math.sin(
        x * wave.frequency * configToUse.sineWaves.primary.frequency +
        timeRef.current * wave.speed * configToUse.globalSpeed * configToUse.sineWaves.primary.speed
      ) * wave.amplitude * configToUse.sineWaves.primary.amplitude;
      const secondaryWave = Math.sin(
        x * wave.frequency * configToUse.sineWaves.secondary.frequency +
        timeRef.current * wave.speed * configToUse.globalSpeed * configToUse.sineWaves.secondary.speed
      ) * wave.amplitude * configToUse.sineWaves.secondary.amplitude;
      const tertiaryWave = Math.sin(
        x * wave.frequency * configToUse.sineWaves.tertiary.frequency +
        timeRef.current * wave.speed * configToUse.globalSpeed * configToUse.sineWaves.tertiary.speed
      ) * wave.amplitude * configToUse.sineWaves.tertiary.amplitude;
      const y = waveY + (primaryWave + secondaryWave + tertiaryWave);
      points.push([x, y]);
    }
    const gradient = createHorizontalGradient(ctx, wave.startColor, wave.endColor, index);
    const totalHeight = (configToUse.renderConfig.numLines - 1) * configToUse.renderConfig.lineSpacing;
    const halfHeight = totalHeight / 2;
    for (let lineIndex = 0; lineIndex < configToUse.renderConfig.numLines; lineIndex++) {
      const lineOffset = -halfHeight + (lineIndex * (configToUse.renderConfig.lineSpacing + 1));
      const distanceFromCenter = Math.abs(lineIndex - (configToUse.renderConfig.numLines - 1) / 2) / ((configToUse.renderConfig.numLines - 1) / 2);
      const verticalOpacity = 0.2 + (0.6 * (1 - distanceFromCenter));
      ctx.beginPath();
      ctx.moveTo(points[0][0], points[0][1] + lineOffset);
      for (let i = 1; i < points.length - 2; i++) {
        const xc = (points[i][0] + points[i + 1][0]) / 2;
        const yc = (points[i][1] + points[i + 1][1]) / 2;
        ctx.quadraticCurveTo(
          points[i][0],
          points[i][1] + lineOffset,
          xc,
          yc + lineOffset
        );
      }
      const last = points[points.length - 1];
      ctx.lineTo(last[0], last[1] + lineOffset);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = configToUse.renderConfig.lineWidth;
      ctx.globalAlpha = verticalOpacity;
      ctx.stroke();
    }
  }, []);

  // Draw frame using the current animated config
  const drawFrame = useCallback(() => {
    const configToUse = animatedConfigRef.current || config;
    if (!configToUse) return;
    const canvas = canvasRef.current;
    const ctx = get2dContext(canvas);
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const firstWaveMaxAmplitude = calculateMaxAmplitude(configToUse.waves[0], configToUse.sineWaves);
    const waveHeight = configToUse.renderConfig.numLines * (configToUse.renderConfig.lineWidth + configToUse.renderConfig.lineSpacing) + configToUse.renderConfig.waveSpacing;
    const topspacing = firstWaveMaxAmplitude + 0.5 * waveHeight;
    configToUse.waves.slice(0, configToUse.numWaves).forEach((wave, index) => {
      let waveY = index * (waveHeight/3);
      drawWave(ctx, wave, waveY + topspacing, index, configToUse);
    });
    timeRef.current += 0.016 * configToUse.globalSpeed;
  }, [config, drawWave]);

  // For static config/control panel mode, use the old animation effect
  useEffect(() => {
    if (startConfig && endConfig && cycleConfig) return; // skip if animated mode
    if (!config) return;
    let running = true;
    const animate = () => {
      drawFrame();
      if (running) animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      running = false;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Add theme change observer
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (canvasRef.current) {
        const ctx = get2dContext(canvasRef.current);
        if (ctx) {
          const backgroundColor = getThemeBackgroundColor();
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });

    return () => observer.disconnect();
  }, [getThemeBackgroundColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setCanvasSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      if (!canvas) return;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      const ctx = get2dContext(canvas);
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);
    return () => {
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  return (
    <div className="wave-container" ref={containerRef}>
      <canvas ref={canvasRef} className="wave-canvas" />
    </div>
  );
};

export default WaveBackground;